import { parseChord } from './chord'
import {
  formatNote,
  pitchClass,
  type Letter,
  type NoteName,
  type Notation,
  type FormatOptions,
} from './notes'

/**
 * Tonalidades y ortografía de las notas según el tono (§3.3): al transponer a F se escribe B♭ y
 * no A♯. Todo se razona sobre la línea de quintas (F C G D A E B…), donde cada nota con su
 * alteración tiene una posición única: C = 0, G = 1, F = −1, F♯ = 6, B♭ = −2.
 */
export interface Key {
  tonic: NoteName
  minor: boolean
}

const LINE_OF_FIFTHS: readonly Letter[] = ['F', 'C', 'G', 'D', 'A', 'E', 'B']

const FIFTHS_OF_LETTER: Record<Letter, number> = { F: -1, C: 0, G: 1, D: 2, A: 3, E: 4, B: 5 }

const mod = (value: number, modulus: number) => ((value % modulus) + modulus) % modulus

/** Posición de una nota en la línea de quintas. */
export function fifthsOf(note: NoteName): number {
  return FIFTHS_OF_LETTER[note.letter] + 7 * note.accidental
}

/** Nota que ocupa una posición de la línea de quintas. */
export function noteFromFifths(fifths: number): NoteName {
  const letter = LINE_OF_FIFTHS[mod(fifths + 1, 7)]!
  return { letter, accidental: Math.floor((fifths + 1) / 7) }
}

/** Interpreta «G», «Em», «Sib», «F#m»… como tonalidad. Devuelve null si no lo es. */
export function parseKey(symbol: string): Key | null {
  const parsed = parseChord(symbol)
  if (!parsed || parsed.kind !== 'chord' || parsed.bass) return null
  if (parsed.suffix === '') return { tonic: parsed.root, minor: false }
  if (parsed.suffix === 'm' || parsed.suffix === 'min' || parsed.suffix === '-') {
    return { tonic: parsed.root, minor: true }
  }
  return null
}

export function formatKey(key: Key, notation: Notation, options: FormatOptions = {}): string {
  return formatNote(key.tonic, notation, options) + (key.minor ? 'm' : '')
}

/** Cuántas alteraciones lleva la armadura: positivo sostenidos, negativo bemoles. */
export function signature(key: Key): number {
  return fifthsOf(key.tonic) - (key.minor ? 3 : 0)
}

/**
 * Centro de la ventana de ortografía: el punto de la línea de quintas más cercano al que se
 * escribe cada nota. En una tonalidad mayor, las siete notas diatónicas ocupan de t−1 a t+5. En
 * menor se desplaza hacia los sostenidos para que la sensible (E7 en La menor) salga como G♯.
 */
function centerOf(key: Key | null): number {
  if (!key) return 2
  const tonic = fifthsOf(key.tonic)
  return key.minor ? tonic : tonic + 2
}

/**
 * Escribe una clase de altura con la ortografía que pide la tonalidad: entre las dos o tres
 * formas posibles, la más cercana al centro del tono. Si empatan, el bemol (Ab en Do, no G♯).
 */
export function spellPitchClass(pc: number, key: Key | null): NoteName {
  const center = centerOf(key)
  const base = mod(mod(pc, 12) * 7, 12) // 7 es su propio inverso módulo 12
  let best = base
  let bestDistance = Infinity
  for (let turn = -3; turn <= 3; turn += 1) {
    const candidate = base + 12 * turn
    const distance = Math.abs(candidate - center)
    if (distance < bestDistance || (distance === bestDistance && candidate < best)) {
      best = candidate
      bestDistance = distance
    }
  }
  return noteFromFifths(best)
}

/**
 * Escribe la tónica de una tonalidad con la armadura más corta. Si empatan (F♯ frente a G♭),
 * decide `preferSharp`: al subir se prefiere el sostenido y al bajar el bemol.
 */
export function spellTonic(pc: number, minor: boolean, preferSharp: boolean): NoteName {
  const base = mod(mod(pc, 12) * 7, 12)
  const target = minor ? 3 : 0
  let best = base
  let bestDistance = Infinity
  for (let turn = -3; turn <= 3; turn += 1) {
    const candidate = base + 12 * turn
    const distance = Math.abs(candidate - target)
    const tie = distance === bestDistance && (preferSharp ? candidate > best : candidate < best)
    if (distance < bestDistance || tie) {
      best = candidate
      bestDistance = distance
    }
  }
  return noteFromFifths(best)
}

export function transposeKey(key: Key, semitones: number): Key {
  if (mod(semitones, 12) === 0) return key
  const pc = mod(pitchClass(key.tonic) + semitones, 12)
  return { tonic: spellTonic(pc, key.minor, semitones > 0), minor: key.minor }
}

type Quality = 'major' | 'minor' | 'dim'

function qualityOf(suffix: string): Quality {
  if (/^(dim|º|°|o|ø|m7b5|m7♭5)/.test(suffix)) return 'dim'
  if (suffix.startsWith('m') && !suffix.startsWith('maj')) return 'minor'
  if (suffix.startsWith('min') || suffix.startsWith('-')) return 'minor'
  return 'major'
}

/** Grados diatónicos (semitonos sobre la tónica y calidad esperada) de cada modo. */
const DIATONIC: Record<'major' | 'minor', readonly (readonly [number, Quality])[]> = {
  major: [
    [0, 'major'],
    [2, 'minor'],
    [4, 'minor'],
    [5, 'major'],
    [7, 'major'],
    [9, 'minor'],
    [11, 'dim'],
  ],
  // Menor natural más la dominante mayor de la armónica.
  minor: [
    [0, 'minor'],
    [2, 'dim'],
    [3, 'major'],
    [5, 'minor'],
    [7, 'minor'],
    [7, 'major'],
    [8, 'major'],
    [10, 'major'],
  ],
}

/**
 * Estima la tonalidad de una lista de acordes: gana la que más acordes diatónicos explica, con
 * un empujón si la canción empieza y acaba en la tónica. Es una estimación, no un dato.
 */
export function inferKey(symbols: readonly string[]): Key | null {
  const chords = symbols.flatMap((symbol) => {
    const parsed = parseChord(symbol)
    if (!parsed || parsed.kind !== 'chord') return []
    return [{ pc: pitchClass(parsed.root), quality: qualityOf(parsed.suffix), root: parsed.root }]
  })
  if (chords.length === 0) return null

  let best: { pc: number; minor: boolean; score: number } | null = null
  for (let pc = 0; pc < 12; pc += 1) {
    for (const minor of [false, true]) {
      const degrees = DIATONIC[minor ? 'minor' : 'major']
      const isTonic = (chord: (typeof chords)[number]) =>
        chord.pc === pc && chord.quality === (minor ? 'minor' : 'major')
      let score = 0
      for (const chord of chords) {
        const offset = mod(chord.pc - pc, 12)
        if (degrees.some(([degree, quality]) => degree === offset && quality === chord.quality)) {
          score += 1
        }
        if (isTonic(chord)) score += 0.25
      }
      if (isTonic(chords[0]!)) score += 1.5
      if (isTonic(chords[chords.length - 1]!)) score += 1.5
      if (!best || score > best.score) best = { pc, minor, score }
    }
  }

  const sharps = chords.filter((chord) => chord.root.accidental > 0).length
  const flats = chords.filter((chord) => chord.root.accidental < 0).length
  return { tonic: spellTonic(best!.pc, best!.minor, sharps >= flats), minor: best!.minor }
}
